import DismissRegular from 'owa-fluent-icons-svg/lib/icons/DismissRegular';
import { FluentButton } from 'owa-fluent-v9-shims';
import { focusedOtherRollupAriaLabel } from './Rollup.locstring.json';
import { focusedViewFilterText } from 'owa-locstrings/lib/strings/focusedviewfiltertext.locstring.json';
import { getDensityModeCssClass, getDensityModeString } from 'owa-fabric-theme';
import { getListViewColumnWidths, isSingleLineListView } from 'owa-mail-layout';
import {
    MCL_TWISTY_PERSONA_CONTAINER_WIDTH,
    MCL_TWISTY_PERSONA_ROOMY_CONTAINER_WIDTH,
    MCL_TWISTY_WIDTH_NO_CHECKBOX,
} from 'owa-mail-layout/lib/mailLayoutConstants';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import { observer } from 'owa-mobx-react';
import { otherViewFilterText } from 'owa-locstrings/lib/strings/otherviewfiltertext.locstring.json';
import React from 'react';
import type FocusedViewFilter from 'owa-service/lib/contract/FocusedViewFilter';
import type ReactListViewType from 'owa-service/lib/contract/ReactListViewType';
import isSenderImageEnabled from 'owa-messagelist-layout-option/lib/utils/isSenderImageEnabled';
import shouldAlwaysShowMessageListCheckboxes from 'owa-messagelist-layout-option/lib/utils/shouldAlwaysShowMessageListCheckboxes';
import shouldHideMessageListCheckboxes from 'owa-messagelist-layout-option/lib/utils/shouldHideMessageListCheckboxes';
import loc, { formatToArray } from 'owa-localize';
import type { MailFolderTableQuery, TableView } from 'owa-mail-list-store';
import { closeButton } from 'owa-locstrings/lib/strings/closebutton.locstring.json';
import {
    MailMultiple20Regular,
    MailMultiple16Regular,
    ArrowCircleUpSparkleRegular,
} from '@fluentui/react-icons';
import {
    standardizedFirstColumn,
    clickableRollup,
    column,
    compact,
    dismissButton as styles_dismissButton,
    firstColumnNoTwisty,
    focusedOtherRollup,
    scl,
    mcl,
    full,
    medium,
    secondColumn,
    itemView,
    conversationView,
    slvRollupContainer,
    text,
    threeColumnClickableRollupFull,
    threeColumnClickableRollupMedium,
    threeColumnClickableRollupCompact,
    threeColumnRollupContainer,
    imageContainer,
    imageBackground,
    imageContainerSCLFull,
    imageContainerSCLMedium,
    imageContainerSCLCompact,
    imageContainerSCLCompactMarginAdjust,
    checkboxSpaceReservedSCL,
    imageContainerMCLFull,
    imageContainerMCLMedium,
    imageContainerMCLCompact,
    checkboxSpaceReservedMCL,
    checkboxSpaceReservedMCLCompact,
    imageContainerWithoutImage,
    standardizedSecondColumn,
    hoverColumnMCLAdjustments,
    senderImageOn,
    senderImageOff,
    monTypeRampStyles,
    noCheckboxStyles,
} from './Rollup.scss';
import {
    singleLineViewContainer,
    firstRow,
} from 'owa-mail-listitem-styles/lib/scss/ListViewItemCommonStyles.scss';
import classnames from 'owa-classnames';

export enum RollupType {
    FocusedOther,
    Prioritize,
}

interface RollupStyles {
    containerClassName?: string;
    primaryTextClassName?: string;
    secondaryTextClassName?: string;
    dismissButtonClassName?: string;
    dismissButtonIconClassName?: string;
}

export interface RollupProps {
    onClick: () => void;
    onDismissed?: (evt?: React.MouseEvent<unknown>) => void;
    onRendered?: () => void;
    primaryText: string;
    secondaryText?: string;
    styles?: RollupStyles;
    tableView: TableView;
    rollupType: RollupType;
}

const defaultExport: React.FunctionComponent<RollupProps> = observer(function Rollup(
    props: RollupProps
) {
    const {
        onClick,
        onDismissed: onDismissed_0,
        onRendered,
        primaryText,
        secondaryText,
        styles: customStyles = {},
        tableView,
        rollupType,
    } = props;

    const {
        primaryTextClassName,
        secondaryTextClassName,
        containerClassName,
        dismissButtonClassName,
        dismissButtonIconClassName,
    } = customStyles;

    const density = getDensityModeString();
    const densityModeCssClass = getDensityModeCssClass(full, medium, compact);
    const primaryTextClassNames = classnames(text, primaryTextClassName);
    const secondaryTextClassNames = classnames(text, secondaryTextClassName);
    const dismissButtonClassNames = classnames(styles_dismissButton, dismissButtonClassName);
    const senderImageEnabled = isSenderImageEnabled();
    const shouldAlwaysShowCheckboxes = shouldAlwaysShowMessageListCheckboxes();
    const hasSenderImageOrPersistentCheckbox = senderImageEnabled || shouldAlwaysShowCheckboxes;
    const tableChecked = tableView?.isInCheckedMode || tableView?.isInSelectAllState;
    const isItemView = tableView?.tableQuery.listViewType === 1;
    const focusedViewFilter =
        (tableView?.tableQuery as MailFolderTableQuery)?.focusedViewFilter ?? -1;
    const isSingleLineView = isSingleLineListView();
    const shouldAllowMultiLinePreview = isGlobalFeatureEnabled('tri-mlMultiLinePreview');

    const { senderColumnWidth } = getListViewColumnWidths(shouldHideMessageListCheckboxes());
    // Non-ad rows hoist the twisty + persona into a leading sibling column when the
    // MCL multi-line preview flight is on. The rollup keeps its icon inside firstColumn,
    // so widen it by the twisty+persona container width to keep downstream columns aligned.
    const twistyPersonaContainerWidth = shouldHideMessageListCheckboxes()
        ? MCL_TWISTY_WIDTH_NO_CHECKBOX
        : getDensityModeString() === 'full'
        ? MCL_TWISTY_PERSONA_ROOMY_CONTAINER_WIDTH
        : MCL_TWISTY_PERSONA_CONTAINER_WIDTH;
    const firstColumnWidth = shouldAllowMultiLinePreview
        ? senderColumnWidth + twistyPersonaContainerWidth
        : senderColumnWidth;
    const firstColumnStyles = React.useMemo(() => {
        return {
            flexBasis: firstColumnWidth,
            width: firstColumnWidth,
            maxWidth: firstColumnWidth,
        };
    }, [firstColumnWidth]);

    React.useEffect(() => {
        onRendered?.();
    }, []);

    const dismissButtonIconProps = React.useMemo(() => {
        return {
            iconName: DismissRegular,
            styles: {
                root: dismissButtonIconClassName,
            },
        };
    }, [dismissButtonIconClassName]);

    const dismissButtonStyles = React.useMemo(() => {
        return {
            icon: {
                lineHeight: '0px',
                margin: '0px',
            },
        };
    }, [dismissButtonIconClassName]);

    const onDismissed = React.useCallback(
        (evt: React.MouseEvent<unknown>) => {
            evt.stopPropagation();
            onDismissed_0?.(evt);
        },
        [onDismissed_0]
    );

    const dismissButton = onDismissed_0 && (
        <FluentButton
            appearance="transparent"
            className={dismissButtonClassNames}
            onClick={onDismissed}
            iconProps={dismissButtonIconProps}
            aria-label={loc(closeButton)}
            title={loc(closeButton)}
            styles={dismissButtonStyles}
        />
    );

    const prioritizeIconStyles = React.useMemo(() => {
        const iconSize = density === 'full' ? '20px' : '16px';
        return {
            fontSize: iconSize,
        };
    }, [density]);

    const icon = React.useMemo(() => {
        if (rollupType === RollupType.Prioritize) {
            return <ArrowCircleUpSparkleRegular style={prioritizeIconStyles} />;
        }

        return density === 'full' ? <MailMultiple20Regular /> : <MailMultiple16Regular />;
    }, [density, rollupType, prioritizeIconStyles]);

    const iconMCL = React.useMemo(() => {
        const classNames = classnames(
            imageContainer,
            getDensityModeCssClass(
                imageContainerMCLFull,
                imageContainerMCLMedium,
                imageContainerMCLCompact
            ),
            hoverColumnMCLAdjustments
        );

        return (
            <div className={classNames}>
                <div className={imageBackground}>{icon}</div>
            </div>
        );
    }, [density, icon]);

    const iconSCL = React.useMemo(() => {
        const compactImageMarginAdjust =
            density === 'compact' &&
            (isGlobalFeatureEnabled('tri-mlCheckboxBehavior') ||
                (isGlobalFeatureEnabled('mon-type-ramp') && senderImageEnabled))
                ? imageContainerSCLCompactMarginAdjust
                : undefined;
        // Always show icon for Prioritize rollup type
        if (rollupType === RollupType.Prioritize) {
            const classNames = classnames(
                imageContainer,
                isGlobalFeatureEnabled('mon-type-ramp') ? monTypeRampStyles : undefined,
                getDensityModeCssClass(
                    imageContainerSCLFull,
                    imageContainerSCLMedium,
                    imageContainerSCLCompact
                )
            );
            return (
                <div className={classNames}>
                    <div className={imageBackground}>{icon}</div>
                </div>
            );
        }

        if (
            (!hasSenderImageOrPersistentCheckbox && !tableChecked) ||
            (!hasSenderImageOrPersistentCheckbox &&
                tableChecked &&
                tableView?.isOnlyOptimisticallyInCheckedMode)
        ) {
            return null;
        }

        // If sender images aren't enabled, but table is in checked mode, then
        // we need to reserve space for the checkbox so that the rollup is aligned
        // with the rest of the items in the list.
        if (!senderImageEnabled) {
            return (
                <div
                    className={classnames(
                        imageContainer,
                        imageContainerWithoutImage,
                        shouldAlwaysShowCheckboxes && senderImageOn,
                        shouldAlwaysShowCheckboxes && compactImageMarginAdjust,
                        isGlobalFeatureEnabled('mon-type-ramp') ? monTypeRampStyles : undefined,
                        getDensityModeCssClass(
                            imageContainerSCLFull,
                            imageContainerSCLMedium,
                            imageContainerSCLCompact
                        )
                    )}
                />
            );
        }

        const classNames = classnames(
            imageContainer,
            senderImageOn,
            compactImageMarginAdjust,
            isGlobalFeatureEnabled('mon-type-ramp') ? monTypeRampStyles : undefined,
            getDensityModeCssClass(
                imageContainerSCLFull,
                imageContainerSCLMedium,
                imageContainerSCLCompact
            )
        );

        return (
            <div className={classNames}>
                <div className={imageBackground}>{icon}</div>
            </div>
        );
    }, [
        senderImageEnabled,
        shouldAlwaysShowCheckboxes,
        hasSenderImageOrPersistentCheckbox,
        density,
        tableChecked,
        tableView?.isOnlyOptimisticallyInCheckedMode,
        icon,
        rollupType,
    ]);

    const renderSingleLineViewRollup = () => {
        return (
            <div
                className={classnames(
                    singleLineViewContainer,
                    slvRollupContainer,
                    containerClassName,
                    densityModeCssClass
                )}
                id={'slvRollupContainer'}
            >
                {/* eslint-disable-next-line react/button-has-type -- (https://aka.ms/OWALintWiki)
                 * Baseline, DO NOT COPY-PASTE!, change this line with a justification or add proper type prop to the button between "button", "submit", or "reset" https://github.com/jsx-eslint/eslint-plugin-react/blob/master/docs/rules/button-has-type.md
                 *	> Missing an explicit type attribute for button */}
                <button
                    className={classnames(clickableRollup, densityModeCssClass, firstRow)}
                    onClick={onClick}
                    aria-label={getAriaLabel(primaryText, secondaryText ?? '', focusedViewFilter)}
                >
                    <div
                        className={classnames(
                            standardizedFirstColumn,
                            firstColumnNoTwisty,
                            shouldHideMessageListCheckboxes() && noCheckboxStyles,
                            isItemView && itemView,
                            focusedOtherRollup,
                            mcl,
                            densityModeCssClass,
                            getDensityModeCssClass(
                                checkboxSpaceReservedMCL,
                                checkboxSpaceReservedMCL,
                                checkboxSpaceReservedMCLCompact
                            )
                        )}
                        style={firstColumnStyles}
                    >
                        {!shouldHideMessageListCheckboxes() && iconMCL}
                        <span className={primaryTextClassNames}>{primaryText}</span>
                    </div>
                    <div
                        className={classnames(
                            standardizedSecondColumn,
                            secondColumn,
                            isItemView ? itemView : conversationView,
                            densityModeCssClass
                        )}
                    >
                        <div className={secondaryTextClassNames}>{secondaryText}</div>
                    </div>
                </button>
                {dismissButton}
            </div>
        );
    };

    const renderThreeColumnViewRollup = () => {
        return (
            <div
                className={classnames(
                    densityModeCssClass,
                    threeColumnRollupContainer,
                    containerClassName,
                    !hasSenderImageOrPersistentCheckbox && senderImageOff
                )}
                id={'threeColumnRollupContainer'}
            >
                {iconSCL}
                {/* eslint-disable-next-line react/button-has-type -- (https://aka.ms/OWALintWiki)
                 * Baseline, DO NOT COPY-PASTE!, change this line with a justification or add proper type prop to the button between "button", "submit", or "reset" https://github.com/jsx-eslint/eslint-plugin-react/blob/master/docs/rules/button-has-type.md
                 *	> Missing an explicit type attribute for button */}
                <button
                    className={classnames(
                        clickableRollup,
                        getDensityModeCssClass(
                            threeColumnClickableRollupFull,
                            threeColumnClickableRollupMedium,
                            threeColumnClickableRollupCompact
                        ),
                        column,
                        focusedOtherRollup,
                        scl,
                        (tableChecked ||
                            hasSenderImageOrPersistentCheckbox ||
                            rollupType === RollupType.Prioritize) &&
                            checkboxSpaceReservedSCL,
                        densityModeCssClass
                    )}
                    onClick={onClick}
                    aria-label={getAriaLabel(primaryText, secondaryText ?? '', focusedViewFilter)}
                >
                    <div className={primaryTextClassNames} title={primaryText}>
                        {primaryText}
                    </div>
                    <div className={secondaryTextClassNames} title={secondaryText}>
                        {secondaryText}
                    </div>
                </button>
                {dismissButton}
            </div>
        );
    };

    return isSingleLineView ? renderSingleLineViewRollup() : renderThreeColumnViewRollup();
},
'Rollup');
export default defaultExport;

function getAriaLabel(
    primaryText: string,
    secondaryText: string,
    focusedViewFilter: FocusedViewFilter
) {
    const viewFilterToSwitchTo =
        focusedViewFilter == 0 ? otherViewFilterText : focusedViewFilterText;

    return formatToArray(
        `{0} {1} {2}`,
        primaryText,
        secondaryText,
        loc(focusedOtherRollupAriaLabel, loc(viewFilterToSwitchTo))
    ).join(' ');
}
